/*
 * DatagridRowUpdater component specific for Adalia M&E.
 *
 * This component consists of:
 * - A select component that allows users to choose one of the DataGrid columns as a "Lookup Select"
 *
 * Features:
 * - Users can drag and drop DataGrid/Static columns into the "Datagrid Row" componen.
 * - Upon changing the value of the Lookup Select, the component fetches and loads all relevant values from the selected row into designated form components
 */
import React from 'react';
import update from 'immutability-helper';

import { fetchJson } from '../../../../helpers/fetch.js';
import {FormContext} from '../../form_context.jsx';

import {
  DataType,
  castDataType,
} from '../../common.jsx';
import Helpers from '../helpers.jsx';
import Setting from '../setting.jsx';

import DropdownSelectionWithAsyncOptions from '../dropdown_selection_with_async_options.jsx';
import GridLayout from '../grid_layout.jsx';
import Resolver from '../../component_resolver.jsx'

import {resolveComponentProps} from '../../form_viewer.jsx';

import {
  datagrid_get_row_for_programme_form_path,
  datagrid_get_rows_for_specific_column_path
} from '~/routes';

export default class AdaliaDatagridRowUpdater extends GridLayout {

  static get CATEGORY() {
    return 'question';
  }

  static get TYPE() {
    return 'adalia_me_datagrid_row_updater';
  }

  static get TRANSLATED_ATTRIBUTES() {
    return {
      label: 'component_title_adalia_me_datagrid_row_updater',
      description: 'field_default_description',
      help: null,
      blankOptionLabel: 'select_blank_option_default'
    };
  }

  static icon() {
    return <i className='fa fa-table-list'></i>;
  }

  constructor(props) {
    super(props);
    this.addRow = this.addRow.bind(this);
    this.deleteRow = this.deleteRow.bind(this);
    this.addColumn = this.addColumn.bind(this);
    this.deleteColumn = this.deleteColumn.bind(this);
    this.reorderOption = this.reorderOption.bind(this);
    this.state = {
      loading: false,
      answers: props.answers || {}
    };
  }

  static defaultContent(language) {
    return Object.assign(super.defaultContent(language), {
      level: 'project',
      name: 'datagrid_row_updater_' + Helpers.randomString(),
      rows: [
        [{span: 1, component: Object.assign(AdaliaDatagridRowSelector.defaultContent(language), {})}],
        [{span: 1, component: Object.assign(AdaliaDatagridRowGridLayout.defaultContent(language), {})}]
      ]
    });
  }

  componentDidMount() {
    super.componentDidMount();

    if (typeof this.props.updateSettingsCallback == 'function') {
      let datagridId = this.props.content.datagridId;
      let dgRowSelector = this.props.content.rows[0][0].component;
      let dgRowLayout = this.props.content.rows[1][0].component;
      let content = update(this.props.content, {
        rows: {
          [0]: {
            [0]: {$merge: Object.assign(dgRowSelector, {columnOptions: this.props.content.columnOptions})},
          },
          [1]: {
            [0]: {$merge: Object.assign(dgRowLayout, {datagridId: datagridId})}
          }
        }
      });
      this.props.updateSettingsCallback(content.id, content);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    super.componentDidUpdate();

    if (prevProps.answers !== this.props.answers) {
      this.setState({answers: this.props.answers});
    }
  }

  answersChanged = (answers, selectedRow) => {
    this.setState({loading: true});
    let newAnswers = this.mappingNewAnswers(answers, selectedRow);

    this.setState(prevState => ({answers: { ...prevState.answers, ...newAnswers}, loading: false}));
  };

  extractReportKeys = (obj) => {
    let reportKeys = {};

    const traverse = (node) => {
      // Check if the current node has a reportKey property
      if (node?.hasOwnProperty('reportKey')) {
        reportKeys[node.reportKey] = node.name;
      }

      // If the current node is an object, loop through its keys
      for (let key in node) {
        if (node?.hasOwnProperty(key) && typeof node[key] === 'object') {
          // Recursive call for nested objects/arrays
          traverse(node[key]);
        }
      }
    };

    // Start the traversal from the root object
    traverse(obj);

    return reportKeys;
  };

  mappingNewAnswers = (answers, selectedRow) => {
    var result = {};
    var content = this.extractReportKeys(this.props.content);

    for (var key in content) {
      var field = content[key];
      if (answers[key] !== undefined) {
        result[field] = selectedRow ? answers[key] : '';
      }
    }

    if (this.props.repeatedFieldScope) {
      // Step 1: Extract the main field name and the nested key
      var match = this.props.repeatedFieldScope.match(/^(.*?)\[(.*?)\]$/);

      if (match) {
        let mainField = match[1];
        let nestedField = match[2];

        // Step 2: Construct the nested object structure
        return {
          [mainField]: {
            [nestedField]: result
          }
        };
      }
    }

    return result;
  };

  renderView() {
    const lookupComponent = Resolver.createElement(AdaliaDatagridRowSelector.TYPE,
      resolveComponentProps(this.props.content.rows[0][0].component, this.props, {
        swapComponentsOnDrop: false,
        selectCallback: this.props.selectCallback,
        selectedId: this.props.selectedId,
        deleteRepeatableRegionAnswerCallback: this.props.deleteRepeatableRegionAnswerCallback,
        fieldScope: this.props.fieldScope,
        repeatedFieldScope: this.props.repeatedFieldScope,
        datagridId: this.props.content.datagridId,
        answersChanged: this.answersChanged
      }));

    const gridLayoutComponent = Resolver.createElement(AdaliaDatagridRowGridLayout.TYPE,
      resolveComponentProps(this.props.content.rows[1][0].component, {...this.props, answers: this.state.answers, readOnly: true}, {
        swapComponentsOnDrop: true,
        selectCallback: this.props.selectCallback,
        selectedId: this.props.selectedId,
        deleteRepeatableRegionAnswerCallback: this.props.deleteRepeatableRegionAnswerCallback,
        fieldScope: this.props.fieldScope,
        repeatedFieldScope: this.props.repeatedFieldScope,
        datagridId: this.props.content.datagridId,
        ...this.componentProps(this.props.content.rows[1][0].component),
        answers: this.state.answers
    }));

    const components =
      <div
        style={{
          backgroundColor: '#8880800a',
          border: '1px solid #00000033',
          borderRadius: '5px',
          boxShadow: '0 0 10px #00000033',
          padding: '0 15px'
        }}
        className='grid_layout'
      >
        {gridLayoutComponent}
        <input type='hidden' name={(this.props.repeatedFieldScope ? `${this.props.repeatedFieldScope}[` : '') + (this.props.fieldScope ? `${this.props.fieldScope}[` : '') + 'datagrid_row-' + this.props.content.datagridId + '-datagrid_row_id' + ']'} value={this.state.answers[this.props.content.rows[0][0].component.name]||''} />
      </div>;

    return (
      <div>
        {lookupComponent}
        {this.state.loading ? <></> : components}
      </div>
    );
  }

  renderSettings() {
    return <div>
      { this.props.content.datagridName &&
        <>
          <label>{I18n.t('datagrid')}</label>
          <p>{this.props.content.datagridName}</p>
        </>
      }
    </div>
  }
}

export class AdaliaDatagridRowSelector extends DropdownSelectionWithAsyncOptions {
  static contextType = FormContext;

  static get TYPE() {
    return 'adalia_me_datagrid_row_selector';
  }

  static get TRANSLATED_ATTRIBUTES() {
    return {
      label: 'component_title_adalia_me_datagrid_row_updater',
      description: 'field_default_description',
      help: null,
      blankOptionLabel: 'select_blank_option_default'
    };
  }

  static dropAllowed(dropArea, droppedContent, components, isNested) {
    return false
  }

  static defaultContent(language) {
    return Object.assign(super.defaultContent(language), {
      deletable: false,
      sortable: false,
      allowMultiple: false,
      reportKey: '',
      dataType: DataType.INTEGER,
      includeBlank: false,
      blankOptionLabel: 'blankOptionLabel'
    });
  }

  componentDidMount() {
    super.componentDidMount();
    this.setState({loading: false});

    let projectId = this.context.projectId || jQuery('#report-container').data('project-id');
    let columnSelectorId = this.props.content.columnSelectorId;

    if (projectId && columnSelectorId) {
      fetchOptionsForSelect(
        projectId,
        this.props.datagridId,
        columnSelectorId,
        this.context.programmeFormId,
        this.getAnswer(),
        this.props.handleDisbursement,
        (res) => {
            if (res) this.setState({options: res})
        }
      );
    }
  }

  onChange = (value) => {
    this.updateAnswer(value);

    let projectId = this.context.projectId || jQuery('#report-container').data('project-id');
    let datagridId = this.props.datagridId;

    if (datagridId == '' || value == '') return this.props.answersChanged({}, value);

    fetchJson(datagrid_get_row_for_programme_form_path({id: datagridId, datagrid_row_id: value, project_id: projectId, programme_form_id: this.context.programmeFormId, _options: true}))
      .then(
      (res) => {
          this.props.answersChanged(res, value);
      }
    )
  };

  componentDidUpdate(prevProps, prevState) {
    super.componentDidUpdate();
  }

  renderSettings() {
    return <div>
      <Setting.Dropdown
        name='columnSelectorId'
        label={I18n.t('column_selector', {scope: 'fe'})}
        value={this.props.content.columnSelectorId}
        onChange={(e) => this.updateSelectedColumn(e.target.value)}
        options={this.props.content.columnOptions}
      />
      <Setting.TextField name='label' label={I18n.t('field_label', {scope: 'fe'})} value={this.getText('label')} onChange={this.updateSettings} />
      <Setting.Checkbox name='label_hidden' className='label-hidden-check' label={I18n.t('field_label_hide', {scope: 'fe'})} value={this.props.content.label_hidden} onChange={this.updateSettings} />
      <Setting.LongTextField name='description' label={I18n.t('field_description', {scope: 'fe'})} value={this.getText('description')} onChange={this.updateSettings} />
      <Setting.LongTextField name='help' label={I18n.t('field_help', {scope: 'fe'})} value={this.getText('help')} onChange={this.updateSettings} />
      <Setting.Checkbox name='includeBlank' label={I18n.t('field_include_blank', {scope: 'fe'})} value={this.props.content.includeBlank} onChange={this.updateSettings} />
      <Setting.TextField name='blankOptionLabel' label={I18n.t('select_blank_option_label', {scope: 'fe'})} value={this.getText('blankOptionLabel')} onChange={this.updateSettings} />
      <Setting.TextField
        name='reportKey'
        label={I18n.t('field_report_key', {scope: 'fe'})}
        value={this.props.content.reportKey}
        description={I18n.t('field_report_key_description', {scope: 'fe'})}
        {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
      />
    </div>
  }

  beforeUpdateSettings(content) {
    super.beforeUpdateSettings(content);
    content.columnSelectorId = castDataType(content.columnSelectorId, DataType.INTEGER);
  }

  updateSelectedColumn(columnSelectorId) {
    let content = JSON.parse(JSON.stringify(this.props.content));
    content.columnSelectorId = columnSelectorId;
    this.props.updateSettingsCallback(this.props.content.id, content, true);
  }
}

export class AdaliaDatagridRowGridLayout extends GridLayout {

  static get TYPE() {
    return 'adalia_me_datagrid_row_grid_layout';
  }

  static defaultContent(language) {
    return Object.assign(super.defaultContent(language), {
      deletable: false,
      sortable: false,
      rows: [
        [{span: 1, component: null}]
      ]
    });
  }

  static dropComponent(dropArea, targetContent, droppedContent, components, isNested) {
    if (droppedContent.datagridId && targetContent.datagridId != droppedContent.datagridId && targetContent.id == dropArea.getAttribute('data-component-id')) {
      alert(I18n.t('component_belongs_to_another_data_grid', {scope: 'fe'}));
      return false;
    }

    if ([AdaliaDatagridRowUpdater.TYPE].includes(droppedContent.type))
      return false;

    if (droppedContent.datagridId && !droppedContent.name.includes('datagrid_row')) {
      let name = `datagrid_row-${droppedContent.datagridId}-${droppedContent.name}`;
      if (droppedContent.rows && droppedContent.rows[0])
        droppedContent.rows[0][0].component.name = name;
      else
        droppedContent.name = name;
    }

    return super.dropComponent(dropArea, targetContent, droppedContent, components, isNested);
  }

  getAnswerByComponentName = (data, key) => {
    // Split the key into parts
    const parts = key.split(/[\[\]]/).filter(part => part);

    let value = data;

    for (let part of parts) {
      if (value[part] !== undefined) {
        value = value[part];
      } else {
        return undefined;
      }
    }

    return value;
  };

  renderView() {
    let rows = this.props.content.rows.map((row, rowIdx) => {
      let cols = row.map((col, colIdx) => {
        let component = null;

        if (col.component) {
          component = Resolver.createElement(col.component.type,
            resolveComponentProps(col.component, this.props, {
              swapComponentsOnDrop: true,
              selectCallback: this.props.selectCallback,
              selectedId: this.props.selectedId,
              deleteRepeatableRegionAnswerCallback: this.props.deleteRepeatableRegionAnswerCallback,
              fieldScope: this.props.fieldScope,
              repeatedFieldScope: this.props.repeatedFieldScope,
              datagridId: this.props.datagridId,
              ...this.componentProps(col.component),
              answers: this.props.answers
            })
          );

          this.props.findComponentsCallback({dataType: [DataType.INTEGER, DataType.DECIMAL, DataType.DATE, DataType.STRING]}).filter((v) => v.sourcesOfPrefill).forEach((c) => {
            if (typeof this.props.updateAnswerCallback == 'function') {
              var componentvalue = '';
              var componentName = c.name;

              if (component.props.repeatedFieldScope) {
                componentName = `${component.props.repeatedFieldScope}[${c.name}]`;
                componentvalue = this.getAnswerByComponentName(this.props.answers, `${component.props.repeatedFieldScope}[${c.sourcesOfPrefill}]`);
              } else {
                componentvalue = this.props.answers[c.sourcesOfPrefill];
              }

              if (componentvalue !== undefined) {
                setTimeout(() => this.props.updateAnswerCallback(`${component.props.fieldScope}[${componentName}]`, componentvalue), 50);
              }
            }
          });

        } else if (this.inEditMode) {
          component = (<Helpers.DropArea componentId={this.props.content.id} context={rowIdx + '-' + colIdx} />);
        }
        return (
          <div
            key={colIdx}
            className={`grid-col ${this.renderComponentCol(component) ? '' : 'd-none'}`}
            style={this.props.asWebView ? {flex: col.span, flexBasis: 'min-content', overflowX: 'auto'} : {flex: col.span}}
            data-flex={col.span}
          >
            {component}
          </div>
        );
      });

      return (
        <div key={rowIdx} className="grid-row" style={this.props.asWebView ? {flexWrap: 'wrap'} : null}>
          {cols}
        </div>
      );
    });

    return (
      <div className='wrapper'>
        {rows}
      </div>
    );
  }
}

const fetchOptionsForSelect = (project_id, datagrid_id, datagrid_col_id, programme_form_id, selected, handlingEnabled, cb) => {
  fetchJson(datagrid_get_rows_for_specific_column_path({ id: datagrid_id, datagrid_col_id: datagrid_col_id, project_id: project_id, programme_form_id: programme_form_id, _options: true })).then(
    (res) => {
        cb(res.map(p => ({ ...p })));
    }
  )
};
